import every from 'lodash/every';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import moment from 'moment-timezone';

import {
  IAutoPolicy,
  TPolicyVehicleFormValues,
  IPolicyVehicle,
  TVehicleVariables,
} from '../../../interfaces';
import getAdditionalInterest from '../getAdditionalInterestObject';
import getLienholderObject from '../getLienholderObject';
import { getRegionSpecificPolicyVehicleVariables } from '../regionUtilities/getRegionSpecificPolicyVehicleVariables';
import getUpdatedVehicleVariables from '../regionUtilities/getUpdatedVehicleVariables';

import { getRegionCodeFromPolicy } from './region';

export type TUpdateVehicleOnPolicy = {
  vehicleToUpdate: IPolicyVehicle;
  updatedData: TPolicyVehicleFormValues | null;
  isDeleting?: boolean | null;
  isUndoDelete?: boolean;
};

export function getPolicyVehicleVinBlacklist(
  editingVehicleVin: string,
  stagedPolicy: IAutoPolicy
) {
  const vehiclesList = stagedPolicy.details.vehicles || [];
  const vinsList = vehiclesList.map((vehicle) => vehicle.vin);
  return vinsList.filter((vin) => vin !== editingVehicleVin);
}

export function getVehicleFromValues(
  values: TPolicyVehicleFormValues,
  stagedPolicy: IAutoPolicy,
  currentVehicle?: IPolicyVehicle
): IPolicyVehicle {
  const additional_interest = getAdditionalInterest(
    values.interested_party_name,
    values.interested_party_address,
    currentVehicle?.additional_interest || null
  );
  const regionCode = getRegionCodeFromPolicy(stagedPolicy);

  if (currentVehicle) {
    return {
      ...currentVehicle,
      vin: values.vin,
      year: Number(values.year),
      make: values.make.toUpperCase(),
      model: values.model.toUpperCase(),
      style: values.style.toUpperCase(),
      ...(values?.trim && {
        trim: String(values.trim).toUpperCase(),
      }),
      automatic_emergency_braking: values.automatic_emergency_braking,
      ownership_status: values.ownership_status,
      years_owned_financed_leased: values.years_owned_financed_leased,
      lienholder: getLienholderObject(
        {
          lienholder_name: values?.lienholder_name || '',
          address_line1: values?.address_line1 || '',
          address_line2: values?.address_line2 || '',
          address_city: values?.address_city || '',
          address_postal: values?.address_postal || '',
          address_region: values?.address_region || '',
        },
        { isPolicyUpdate: true }
      ),
      business_use: values.business_use,
      anti_theft_device: values.anti_theft_device,
      anti_lock_brakes: values.anti_lock_brakes,
      in_storage: values.in_storage,
      passive_restraint_device: values.passive_restraint_device,
      vehicle_category:
        values.vehicle_category || currentVehicle.vehicle_category,
      rideshare_use:
        !!values.rideshare_use ||
        !!currentVehicle.variables.side_hustle ||
        !!currentVehicle.variables.ridesharing,
      variables: getUpdatedVehicleVariables({
        baseVehicleYear: Number(moment(stagedPolicy.starts_at).format('YYYY')),
        garageAddress: currentVehicle.garage_address,
        inStorage: values.in_storage || currentVehicle.in_storage,
        ownershipStatus: values.ownership_status,
        variables: values.variables || currentVehicle.variables,
        year: Number(values.year),
      }) as TVehicleVariables,
      additional_interest,
      vehicle_use: values?.vehicle_use,
      annual_mileage: values?.annual_mileage,
      is_rated_cost_new: values?.is_rated_cost_new,
      cost_new_value: values?.cost_new_value,
    };
  }
  return {
    data_source: 'manual',
    garage_address: omit(stagedPolicy.rating_address, [
      'id',
      'latitude',
      'longitude',
    ]),
    id: Number(values.id) || null,
    vin: values.vin,
    year: Number(values.year),
    make: values.make.toUpperCase(),
    model: values.model.toUpperCase(),
    style: values.style.toUpperCase(),
    vehicle_category: values.vehicle_category,
    ...(values?.trim && {
      trim: String(values.trim).toUpperCase(),
    }),
    automatic_emergency_braking: values.automatic_emergency_braking,
    ownership_status: values.ownership_status,
    years_owned_financed_leased: values.years_owned_financed_leased,
    lienholder: getLienholderObject(
      {
        lienholder_name: values?.lienholder_name || '',
        address_line1: values?.address_line1 || '',
        address_line2: values?.address_line2 || '',
        address_city: values?.address_city || '',
        address_postal: values?.address_postal || '',
        address_region: values?.address_region || '',
      },
      { isPolicyUpdate: true }
    ),
    business_use: values.business_use,
    in_storage: values.in_storage,
    passive_restraint_device: values.passive_restraint_device,
    anti_theft_device: values.anti_theft_device,
    anti_lock_brakes: values.anti_lock_brakes,
    additional_interest,
    ...getRegionSpecificPolicyVehicleVariables(values, regionCode),
    vehicle_use: values?.vehicle_use,
    annual_mileage: values?.annual_mileage,
    is_rated_cost_new: values?.is_rated_cost_new,
    cost_new_value: values?.cost_new_value,
  };
}

export function addNewVehicleToPolicy(
  values: TPolicyVehicleFormValues,
  stagedPolicy: IAutoPolicy
): IAutoPolicy {
  const newVehicle = omit(getVehicleFromValues(values, stagedPolicy), 'id');

  const newVehiclesList = [...stagedPolicy.details.vehicles, newVehicle];

  const updatedPolicy: IAutoPolicy = {
    ...stagedPolicy,
    details: {
      ...stagedPolicy.details,
      vehicles: newVehiclesList,
    },
  };

  return updatedPolicy;
}

export function updateVehicleOnPolicy(
  {
    vehicleToUpdate,
    updatedData = null,
    isDeleting = null,
    isUndoDelete = false,
  }: TUpdateVehicleOnPolicy,
  stagedPolicy: IAutoPolicy
) {
  const updatesToMake = updatedData
    ? getVehicleFromValues(updatedData, stagedPolicy, vehicleToUpdate)
    : {};

  const stagedVehiclesList = stagedPolicy.details.vehicles;

  const isVehicleSavedToPolicy = !!vehicleToUpdate?.id;

  const updatedVehiclesList =
    isDeleting && !isVehicleSavedToPolicy
      ? stagedVehiclesList.filter(
          (vehicle) => vehicle.vin !== vehicleToUpdate?.vin
        )
      : stagedVehiclesList.map((vehicle) => {
          if (vehicleToUpdate?.vin === vehicle.vin) {
            const updatedVehicle = {
              ...vehicle,
              ...updatesToMake,
            };

            if (!!vehicle.lienholder && !updatedVehicle.lienholder) {
              updatedVehicle.lienholder = {
                ...vehicle.lienholder,
                deleted: true,
              };
            }

            if (isDeleting) {
              return {
                ...updatedVehicle,
                deleted: true,
              };
            }
            return omit(updatedVehicle, ['deleted']);
          }
          return vehicle;
        });

  const hasNoChanges =
    !isDeleting &&
    !isUndoDelete &&
    every(
      updatedVehiclesList.map((vehicle) => {
        const oldVehicle = find(stagedPolicy.details.vehicles, {
          vin: vehicle.vin,
        });
        const oldLienholder = get(oldVehicle, 'lienholder')
          ? {
              ...omit(oldVehicle?.lienholder, [
                'deleted',
                'address.id',
                'address.country.id',
                'address.region.id',
                'address.timezone',
                'address.region.name',
                'address.county',
              ]),
            }
          : null;
        const newLienholder = vehicle?.lienholder
          ? {
              ...vehicle.lienholder,
              address: {
                ...omit(vehicle?.lienholder?.address, [
                  'region.name',
                  'county',
                ]),
                line2: vehicle?.lienholder?.address?.line2 || null,
              },
            }
          : null;

        return isEqual(
          {
            ...vehicle,
            lienholder: newLienholder,
          },
          {
            ...oldVehicle,
            lienholder: oldLienholder,
          }
        );
      }),
      (val) => val === true
    );

  const updatedPolicy = {
    ...stagedPolicy,
    details: {
      ...stagedPolicy.details,
      vehicles: updatedVehiclesList,
    },
  };

  return { updatedPolicy, hasNoChanges, isVehicleSavedToPolicy };
}
